/**
 * The size of a menu as a whole (T14, docs/13-typografie.md): a factor of the menu, not of its style –
 * whether a menu has eight dishes or forty belongs to the menu. From the style and the factor comes
 * the style that print, PDF and Word set with (`scaleDesign`); the menu on the net keeps the style's
 * own sizes (T13).
 */
import { DISH_SPACE, MIN_COLUMN, NUMBER_COLUMN, PRICE_COLUMN, SECTION_SPACE, SIZE_LIMITS, VARIANT_INDENT, pageOf } from "./design.ts";
import type { Design, StyleKey, TextStyle } from "./design.ts";

export interface MenuScale {
  /** The dishes' size, 1 = the style's. */
  size: number;
  /** The space between dishes and above sections, on top of the size. */
  space: number;
  /** The margins: 0 = the style's, 1 = the most (MARGIN_MOST). */
  margin: number;
}

export const SCALE_LIMITS = { size: { min: 0.8, max: 1.4 }, space: { min: 1, max: 2 }, margin: { min: 0, max: 1 } } as const;
export const NO_SCALE: MenuScale = { size: 1, space: 1, margin: 0 };

/** The most the margins get, as shares of the paper: the sides a sixth of its width, the top an eighth of its height. */
export const MARGIN_MOST = { x: 1 / 6, top: 1 / 8, topToX: 1.5 } as const;
/** Between the two columns of „Zweispaltig“, in points (the PDF's). */
export const COLUMN_GAP = 26;
/** Small parts never below this, in points. */
const SMALL = 6.5;

/** The parts of the dishes: they grow with the size. */
const ITEMS: readonly StyleKey[] = ["number", "dish", "description", "variant", "price", "codes", "tags", "itemNote", "sizeHead", "orLine", "text", "textLine", "sectionNote", "menuNote"];
/** The headings over the dishes: they grow more slowly, and stay above the dishes. */
const HEADINGS: readonly StyleKey[] = ["section", "subsection", "course", "part", "textTitle"];
// The head (house, title, lead) and the legend stay as they are.

const between = (v: unknown, lo: number, hi: number, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback);
const tenth = (v: number) => Math.round(v * 10) / 10;

/** A menu's scale as sent or stored; null when it is the style's own. */
export function scaleOf(v: unknown): MenuScale | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const s = {
    size: Math.round(between(o.size, SCALE_LIMITS.size.min, SCALE_LIMITS.size.max, 1) * 1000) / 1000,
    space: Math.round(between(o.space, SCALE_LIMITS.space.min, SCALE_LIMITS.space.max, 1) * 1000) / 1000,
    margin: Math.round(between(o.margin, SCALE_LIMITS.margin.min, SCALE_LIMITS.margin.max, 0) * 1000) / 1000,
  };
  return isNoScale(s) ? null : s;
}

/** What a scale does, said: „Speisen 140 % · Abstände 200 % · Ränder breiter“. */
export function scaleSummary(s: MenuScale): string {
  const pct = (v: number) => `${Math.round(v * 100)} %`;
  return [`Speisen ${pct(s.size)}`, ...(s.space > 1 ? [`Abstände ${pct(s.space)}`] : []), ...(s.margin > 0 ? ["Ränder breiter"] : [])].join(" · ");
}

export const isNoScale = (s: MenuScale | null | undefined) => !s || (s.size === 1 && s.space === 1 && s.margin === 0);

/** The margins at `margin` (mm): the sides toward a sixth of the width, the top toward an eighth of the height. */
export function scaledMargins(d: Design, margin: number, twoColumns = false): { x: number; top: number } {
  const page = pageOf(d.page.format);
  const toMm = (pt: number) => (pt * 25.4) / 72;
  let mostX = Math.max(d.page.marginX, toMm(page.width) * MARGIN_MOST.x);
  // Two columns stay two: each at least MIN_COLUMN wide.
  if (twoColumns) mostX = Math.max(d.page.marginX, Math.min(mostX, toMm((page.width - COLUMN_GAP - 2 * MIN_COLUMN) / 2)));
  const x = d.page.marginX + margin * (mostX - d.page.marginX);
  const mostTop = Math.max(d.page.marginY, Math.min(toMm(page.height) * MARGIN_MOST.top, x * MARGIN_MOST.topToX));
  return { x: tenth(x), top: tenth(d.page.marginY + margin * (mostTop - d.page.marginY)) };
}

/**
 * The style a menu is set with at its scale (T14): the dishes × size; the headings by its square
 * root, but at least 1.2 times the dish's name where they were; head and legend as they are; the
 * space between dishes and above sections × size × space, within their bounds; the margins toward
 * their most, the bottom (where the foot stands) as the style's.
 */
export function scaleDesign(d: Design, s: MenuScale | null | undefined, twoColumns = false): Design {
  if (!s || isNoScale(s)) return d;
  const { size, space, margin } = s;
  const bounded = (v: number) => tenth(Math.min(SIZE_LIMITS.max, Math.max(SIZE_LIMITS.min, v)));
  const item = (t: TextStyle): TextStyle => ({ ...t, size: bounded(Math.max(Math.min(t.size, SMALL), t.size * size)) });
  const heading = (t: TextStyle, dish: number): TextStyle => ({ ...t, size: bounded(Math.max(t.size * Math.sqrt(size), Math.min(t.size * size, 1.2 * dish * size))) });
  const styles = { ...d.styles };
  for (const k of ITEMS) styles[k] = item(d.styles[k]);
  for (const k of HEADINGS) styles[k] = heading(d.styles[k], d.styles.dish.size);
  const drinkStyles = d.drinkStyles ? (Object.fromEntries(Object.entries(d.drinkStyles).map(([k, t]) => [k, item(t)])) as typeof d.drinkStyles) : undefined;
  const setDrinkStyles = d.setDrinkStyles ? (Object.fromEntries(Object.entries(d.setDrinkStyles).map(([k, t]) => [k, item(t)])) as typeof d.setDrinkStyles) : undefined;
  const margins = scaledMargins(d, margin, twoColumns);
  const roomy = size * space;
  return {
    ...d,
    styles,
    ...(drinkStyles ? { drinkStyles } : {}),
    ...(setDrinkStyles ? { setDrinkStyles } : {}),
    dishSpace: tenth(Math.min(Math.max(DISH_SPACE.max, d.dishSpace), Math.max(DISH_SPACE.min, d.dishSpace * roomy))),
    sectionSpace: tenth(Math.min(Math.max(SECTION_SPACE.max, d.sectionSpace), d.sectionSpace * roomy)),
    columns: { number: tenth(Math.min(NUMBER_COLUMN.max, d.columns.number * size)), price: tenth(Math.min(PRICE_COLUMN.max, d.columns.price * size)) },
    variantIndent: tenth(Math.min(VARIANT_INDENT.max, d.variantIndent * size)),
    page: { ...d.page, marginX: margins.x, ...(margin > 0 ? { top: margins.top } : {}) },
  };
}
